import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useStores } from "../../Hooks";
import DomainWrapper from "./DomainWrapper";
import './domainSettings.scss';
import { Button, Paper, TableContainer } from "@mui/material";
import EllipsisContainer from "../Shared/EllipsisContainer";
import { FrontlineCheckbox } from "../Shared/FrontlineCheckbox";
import DeploymentIcon from "../../assets/svg-icons/DeploymentIcon";
import sharedUtils from "../../serverClientShared/sharedUtils";

function ProjectIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="1" y="11.3447" width="6.96554" height="6.96554" strokeWidth="2" />
            <path d="M18.9999 15.1723C18.9999 17.2863 17.2862 18.9999 15.1723 18.9999C13.0584 18.9999 11.3447 17.2863 11.3447 15.1723C11.3447 13.0584 13.0584 11.3447 15.1723 11.3447C17.2862 11.3447 18.9999 13.0584 18.9999 15.1723Z" strokeWidth="2" />
            <path d="M6.60734 8.31037L9.99978 2.08886L13.3922 8.31037H6.60734Z" strokeWidth="2" />
        </svg>
    );
}

const deploymentStatusEnum = {
    IDLE: 'IDLE',
    PROCESSING: 'PROCESSING',
    FAILED: 'FAILED',
    SUCCESS: 'SUCCESS',
    NO_STAGING: 'NO_STAGING'
};

const WorkspaceDeployment = observer(() => {
    const { generalStore, projectStore, modalStore } = useStores();
    const [projectsMap, setProjectsMap] = useState(new Map());
    const [blockUI, setBlockUI] = useState(false);
    const [isDoneTheDeployment, setIsDoneTheDeployment] = useState(false);
    const { isDev, isTest, isBackup } = sharedUtils.getWorkspaceStageState(generalStore.workspace);
    const [isDeploying, setIsDeploying] = useState(true);
    const [isAllChecked, setIsAllChecked] = useState(false);

    const deployToStr = isDev ? 'test' : (isTest || isBackup ? 'prod' : 'dev');

    const changeProject = (projectId, isChecked) => {
        projectsMap.get(projectId).isChecked = isChecked;
        setProjectsMap(new Map(projectsMap));
        setIsAllChecked([...projectsMap.values()].every(project => project.isChecked));
    };

    const deployWorkspace = async () => {
        try {
            setBlockUI(true);
            const projectList = [...projectsMap.entries()]
                .filter(([, { isChecked }]) => isChecked)
                .map(([projectId]) => projectId);

            const promise = generalStore.deployWorkspace(projectList);
            const error = generalStore.translate('could not deploy workspace');
            const success = generalStore.translate('workspace deployment started successfully');
            const loading = `${generalStore.translate('deploying workspace')}, ${generalStore.translate('please do not refresh this page', true)}`;
            await modalStore.runAsync(promise, error, success, loading);
            setIsDeploying(true);
        } catch (e) {
            console.error(e);
            setBlockUI(false);
        }
    };

    const checkStatus = async () => {
        try {
            const status = await generalStore.getDeployStatus();
            setIsDeploying(status && (status.Status === deploymentStatusEnum.IDLE || status.Status === deploymentStatusEnum.PROCESSING));
            if (status?.Status === deploymentStatusEnum.NO_STAGING) {
                setIsDoneTheDeployment(true);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        const fetchProjects = async () => {
            const promise = projectStore.adminGetAllProjects();
            const errorMessage = generalStore.translate('could not fetch projects');
            const loadingMessage = `${generalStore.translate('fetching projects')}...`;

            const allProjects = await modalStore.runAsync(promise, errorMessage, null, loadingMessage);
            allProjects.forEach(project => {
                projectsMap.set(project.ProjectIdentifier, {
                    projectName: project.ProjectName,
                    isChecked: false
                });
            });
            setProjectsMap(new Map(projectsMap));
            await checkStatus();
        };

        fetchProjects();
        // eslint-disable-next-line
    }, [generalStore, projectStore, modalStore]);

    useEffect(() => {
        if (isDeploying) {
            const interval = setInterval(checkStatus, 5000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line
    }, [isDeploying]);

    const anyProjectSelected = [...projectsMap.values()].some(project => project.isChecked);

    if (isDoneTheDeployment) {
        return (
            <DomainWrapper wide>
                <div className='domain-container workspace-deployment'>
                    <div className='domain-header'>{generalStore.translate('deployment')}</div>
                    <div className='domain-secondary'>{generalStore.translate('The deployment is done.')}</div>
                </div>
            </DomainWrapper>
        );
    }

    const handleSelectAll = () => {
        const newCheckedState = !isAllChecked;
        setIsAllChecked(newCheckedState);
        projectsMap.forEach((_, projectId) => {
            changeProject(projectId, newCheckedState);
        });
    };

    return (
        <DomainWrapper wide>
            <div className='domain-container workspace-deployment'>
                <div className='domain-header'>{generalStore.translate('deployment')}</div>
                <button className='select-button' onClick={handleSelectAll}>
                    {isAllChecked ? generalStore.translate('DeselectAll') : generalStore.translate('SelectAll')}
                </button>
                <Button
                    className='primary-button theme-button deployment-button'
                    onClick={deployWorkspace}
                    disabled={blockUI || (!isBackup && !anyProjectSelected) || isDeploying}
                >
                    <DeploymentIcon />
                    {isDeploying
                        ? `${generalStore.translate('Deploying workspace')}...`
                        : `${generalStore.translate('deploy to')} ${generalStore.translate(deployToStr, true)} ${generalStore.translate('workspace', true)}`}
                </Button>
                <div className='domain-secondary'>
                    {isBackup
                        ? `${generalStore.translate('Deploy the backup workspace to the production workspace.')}${generalStore.translate('This will overwrite the production workspace with the backup workspace.')}`
                        : generalStore.translate('Select the projects you would like to deploy and then press on deploy button')}
                </div>
                <div className='domain-users-content'>
                    <TableContainer component={Paper} className='users-table-container theme-windows-and-menus'>
                        {!isDeploying && !isBackup && (
                            <div className='workspace-projects-container theme-windows-and-menus'>
                                {[...projectsMap.entries()].map(([projectId, { projectName, isChecked }]) => (
                                    <div key={projectId} className='project-checkbox-container'>
                                        <div className='project-icon-container'>
                                            <ProjectIcon />
                                            <EllipsisContainer value={projectName}>
                                                <div className='project-name'>{projectName}</div>
                                            </EllipsisContainer>
                                        </div>
                                        <FrontlineCheckbox
                                            isChecked={isChecked}
                                            onChange={isChecked => changeProject(projectId, isChecked)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </TableContainer>
                </div>
            </div>
        </DomainWrapper>
    );
});

export default WorkspaceDeployment;